import React, { useEffect, useState, useCallback } from "react";
import { LinkCard } from "../stories/cards/LinkCard";
import Buy from "../assets/icons/Platform/Buy_tests.svg";
import Calender from "../assets/icons/calender_new.svg";
import PropTypes from "prop-types";
import {
  as_title,
  format_date,
  formatTrackingNumber,
  iconHelper,
  toSentenceCase,
} from "../utils/utils";
import { Button } from "../stories/Buttons/Button";
import { myResultUrl, ResultByBioMarkerUrl } from "../utils/constants";
import useApis from "../services/useAPI";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Spinner, Dropdown } from "react-bootstrap";
import { useMediaQuery } from "react-responsive";
import calenderIcon from "../assets/icons/my-result-calender-icon.png";
import barcodeIcon from "../assets/icons/barcode-icon.png";
import exclamationIcon from "../assets/icons/exclamation-icon.svg";
import ResultByBiomarker from "./ResultByBiomarker";
import { SummaryCard } from "../stories/cards/SummaryCard";
import pkbLogo from "../assets/icons/pkb-icon.svg";
import dotIcon from "../assets/icons/dot.svg";
import appointmentIcon from "../assets/icons/appointment.svg";
import Card from "react-bootstrap/Card";
import { getTestNameFromType } from "../utils";
import TestKitCarousel from "../stories/Carousel/TestKitCarousel";

/**
 * TestList Component
 * Fetches and displays the user's tests and their results.
 */

const ChevronDownIcon = () => {
  return (
    <div className="icon-wrapper">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 640 640"
        width="16"
        height="16"
      >
        <path d="M297.4 470.6C309.9 483.1 330.2 483.1 342.7 470.6L534.7 278.6C547.2 266.1 547.2 245.8 534.7 233.3C522.2 220.8 501.9 220.8 489.4 233.3L320 402.7L150.6 233.4C138.1 220.9 117.8 220.9 105.3 233.4C92.8 245.9 92.8 266.2 105.3 278.7L297.3 470.7z" />
      </svg>
    </div>
  );
};

const TestList = ({ isAppointment = false } = {}) => {
  const { id } = useParams();
  const [testData, setTestData] = useState([]);
  const [isHealthTests, setIsHealthTests] = useState(true);
  const [originalTestData, setOriginalTestData] = useState([]);
  const [resultByBioMarker, setResultByBioMarker] = useState([]);
  const [status, setStatus] = useState("All");
  const [date, setDate] = useState("All time");
  const [sortBy, setSortBy] = useState("Most recent");
  const [isAppointmentsLoading, setIsAppointmentsLoading] = useState(false);
  const [appointmentsFetched, setAppointmentsFetched] = useState(false);
  // const {allTestData, setAllTestData} = useState([])
  const { callApi, isLoading, serverError, resultGet } = useApis();
  const {
    callApi: callResultByBioMarkerApi,
    isLoading: isLoadingResultByBioMarker,
    serverError: serverErrorResultByBioMarker,
    resultGet: resultGetResultByBioMarker,
  } = useApis();
  const navigate = useNavigate();
  const isResponsive = useMediaQuery({ maxWidth: 768 });
  const isTablet = useMediaQuery({ minWidth: 768 });
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isCustoMobile = useMediaQuery({ maxWidth: 620 });
  const DEFAULT_STATUS = "All";
  const DEFAULT_DATE = "All time";
  const DEFAULT_SORT = "Most recent";
  const areFiltersDefault =
    status === DEFAULT_STATUS &&
    date === DEFAULT_DATE &&
    sortBy === DEFAULT_SORT;

  const applyFilters = useCallback(
    (newStatus = status, newDate = date, newOrder = sortBy) => {
      let filtered = [...originalTestData];

      if (newStatus !== "All") {
        const testKitTypes = getTimsStatus(newStatus);
        filtered = filtered.filter((test) =>
          testKitTypes.includes(test.test_kit_status),
        );
      }

      if (newDate !== "All time") {
        const cutoff = new Date();
        if (newDate === "Last 30 days") cutoff.setDate(cutoff.getDate() - 30);
        if (newDate === "Last 12 months")
          cutoff.setMonth(cutoff.getMonth() - 12);
        filtered = filtered.filter(
          (item) => new Date(item.status_ts) >= cutoff,
        );
      }

      filtered.sort((a, b) => {
        const dateA = new Date(a.status_ts ?? 0);
        const dateB = new Date(b.status_ts ?? 0);
        return newOrder === "Least recent" ? dateA - dateB : dateB - dateA;
      });

      setTestData(filtered);
    },
    [originalTestData],
  );

  useEffect(() => {
    applyFilters(status, date, sortBy);
  }, [originalTestData]);

  const getStatusDetails = (timsStatus) => {
    const statusMap = {
      activated: { statusUIName: "Registered", bgColor: "#D1E6E2" },
      "quarantine-cleared": { statusUIName: "Registered", bgColor: "#D1E6E2" },
      "quarantine_cleared": { statusUIName: "Registered", bgColor: "#D1E6E2" },
      activating: { statusUIName: "Registration pending", bgColor: "#F6DBD5" },
      quarantined: { statusUIName: "Registration pending", bgColor: "#F6DBD5" },
      processing: { statusUIName: "Processing", bgColor: "#D1E6E2" },
      "report-in-review": {
        statusUIName: "Under doctor review",
        bgColor: "#D1E6E2",
      },
      "report-ready": { statusUIName: "Results ready", bgColor: "#D1E6E2" },
      published: { statusUIName: "Results ready", bgColor: "#D1E6E2" },
      error: { statusUIName: "Unable to report", bgColor: "#F6DBD5" },
      rejected: { statusUIName: "Unable to report", bgColor: "#F6DBD5" },
      archived: { statusUIName: "Unable to report", bgColor: "#F6DBD5" },
      "archived": { statusUIName: "Unable to report", bgColor: "#F6DBD5" },
      "rejected": { statusUIName: "Unable to report", bgColor: "#F6DBD5" },
      rejected_internal: {
        statusUIName: "Unable to report",
        bgColor: "#F6DBD5",
      },
      "rejected-internal": {
        statusUIName: "Unable to report",
        bgColor: "#F6DBD5",
      },
    };
    return (
      statusMap[timsStatus] || { statusUIName: "Unknown", bgColor: "white" }
    );
  };

  const getTimsStatus = (statusUIName) => {
    const statusMap = {
      Registered: ["activated", "quarantine-cleared"],
      "Registration pending": ["activating", "quarantined"],
      Processing: ["processing"],
      "Under doctor review": ["report-in-review"],
      "Results ready": ["report-ready", "published"],
      "Unable to report": ["error", "rejected_internal", "rejected-internal"],
    };

    for (const [key, value] of Object.entries(statusMap)) {
      if (key === statusUIName) {
        return value;
      }
    }
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);

    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "long" });
    const year = date.getFullYear();

    const getOrdinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };

    return `${day}${getOrdinal(day)} ${month} ${year}`;
  };

  const getButtonText = (booking_id, registration_stage) => {
    if (booking_id) {
      return registration_stage === "assessment"
        ? "View registration"
        : "Continue";
    }

    return "";
  };

  const getContinueBookingLink = (stage, bookingId, testType) => {
    switch (stage) {
      case "type-confirm":
        return `/bloods/boots/${testType}/booking/confirm-details/${bookingId}/`;
      case "consent":
        return `/bloods/boots/${testType}/booking/confirm-details/${bookingId}/`;
      case "personal-details":
        return `/bloods/boots/assessment/${testType}/${bookingId}/1`;
      default:
        return null;
    }
  };

  const getBookingLink = (stage, testId, bookingStatus, testType) => {
    const isAssessment = stage === "assessment";
    const isRejected = bookingStatus === "rejected";
    const assessmentLink = `/bloods/boots/success-page/${testType}/${testId}`;

    if (isAssessment) {
      return assessmentLink;
    }

    return isRejected ? null : getContinueBookingLink(stage, testId, testType);
  };

  useEffect(() => {
    const fetchTestData = async () => {
      try {
        if (isAppointment === true) {
          setIsAppointmentsLoading(true);
        }
        await callApi(null, myResultUrl, "GET");
      } catch (error) {
        console.error("API error:", error);
      } finally {
        setAppointmentsFetched(true);
        if (isAppointment === true) {
          setIsAppointmentsLoading(false);
        }
      }
    };
    fetchTestData();
  }, [id]);

  useEffect(() => {
    if (resultGet?.success) {
      if (isAppointment === true) {
        setTestData(resultGet?.phlebotomist_bookings?.data || []);
        setOriginalTestData([
          ...(resultGet?.phlebotomist_bookings?.data || []),
        ]);
      } else if (isAppointment === false) {
        setTestData(resultGet?.test_kits?.data || []);
        setOriginalTestData(resultGet?.test_kits?.data);
      }
    }
  }, [resultGet]);

  const handleResultByBiomarker = async (mode) => {
    try {
      await callResultByBioMarkerApi(null, ResultByBioMarkerUrl, "GET");
    } catch (error) {
      handleSection(mode);
      return;
    }
    handleSection(mode);
  };

  useEffect(() => {
    if (resultGetResultByBioMarker?.success) {
      setResultByBioMarker(resultGetResultByBioMarker?.data);
    }
  }, [resultGetResultByBioMarker]);

  if (
    isAppointment === true &&
    (isLoading || isAppointmentsLoading) &&
    !serverError
  ) {
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          role="output"
          data-testid="spinner"
          variant="info"
          className="my-custom-spinner"
        />
      </div>
    );
  }

  if (isLoading || (isLoadingResultByBioMarker && !serverError)) {
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          role="output"
          data-testid="spinner"
          variant="info"
          className="my-custom-spinner"
        />
      </div>
    );
  }
  if (!appointmentsFetched && !serverError) {
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          role="output"
          data-testid="spinner"
          variant="info"
          className="my-custom-spinner"
        />
      </div>
    );
  }
  if (serverError || serverErrorResultByBioMarker) {
    return (
      <p className="text-muted">
        {serverError || serverErrorResultByBioMarker}
      </p>
    );
  }

  const getTitle = (test_type) => {
    let title = toSentenceCase(getTestNameFromType(test_type));
    // known case of vitamin B12 test
    if (title?.toLowerCase() === "vitamin b12 test") {
      title = "Vitamin B12 Test";
    }

    return title;
  };

  const changeTestStatus = (newStatus) => {
    setStatus(newStatus);
    applyFilters(newStatus, date, sortBy);
  };
  const setTestsByDate = (newDate) => {
    setDate(newDate);
    applyFilters(status, newDate, sortBy);
  };
  const setTestOrder = (newOrder) => {
    setSortBy(newOrder);
    applyFilters(status, date, newOrder);
  };

  const handleClick = (id) => {
    navigate(`/test-kits/${id}`);
  };

  const handleSection = (status) => {
    setIsHealthTests(status);
  };
  return isAppointment === true ? (
    <div className="activate-box-wrapper">
      <div className="row">
        <div className="col-md-12">
          {appointmentsFetched &&
          testData?.length === 0 &&
          !isLoading &&
          !isAppointmentsLoading ? (
            <div className="d-flex flex-column align-items-center no-appointments-page">
              <div className="image-wrapper d-flex justify-content-center align-items-center">
                <img src={Calender} alt="No Appointments" />
              </div>
              <h1 className="text-center mb-3" style={{ color: "#003032" }}>
                No active appointments
              </h1>
              <p className="text-muted">
                You don’t currently have any open appointments. If you’ve
                already visited the store for your appointment, you can go to My
                Results to track progress or view your results.
              </p>
            </div>
          ) : (
            <div>
              <h1 className="text-left mb-3" style={{ color: "#003032" }}>
                My Appointments
              </h1>
              <hr style={{ color: "#CCD8DF" }}></hr>
              <p className="text-muted-custom">
                Below is a summary of your current appointments.
              </p>
              {testData.map((test) => (
                <div data-testid={`test-${test.id}`} key={test.id}>
                  {/* <SummaryCard test={test} /> */}
                  <Card className="appointment-card">
                    <Card.Body
                      className={`d-flex ${isMobile && "flex-column"}`}
                    >
                      <div
                        className={`appointment-details-wrapper ${
                          !isMobile && "col-6"
                        }`}
                      >
                        <div className="appointment-status">
                          <span>
                            <img
                              src={dotIcon}
                              alt="dot icon"
                              className="me-1"
                            />
                            <span>In progress</span>
                          </span>
                        </div>
                        <div className="title-and-btn d-flex justify-content-between">
                          <div className="appointment-title">
                            <span>{getTitle(test?.test_type)}</span>{" "}
                            <small>In-Store Service</small>
                          </div>
                        </div>
                        <div className="booking-id-details d-flex col-6">
                          <div className="appointment-logo">
                            <img src={appointmentIcon} alt="appointment-icon" />
                          </div>
                          <div className="booking-id d-flex flex-column">
                            <small>BOOKING ID</small>
                            <span>{test?.booking_id}</span>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`view-registraion-btn ${
                          !isMobile &&
                          "d-flex justify-content-end align-items-center col-6"
                        }`}
                      >
                        <Link
                          to={getBookingLink(
                            test?.registration_stage,
                            test?.id,
                            test?.registration_stage,
                            test?.test_type,
                          )}
                        >
                          <button className="btn-green">
                            {getButtonText(
                              test?.booking_id,
                              test?.registration_stage,
                            )}
                          </button>
                        </Link>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (
     testData.length === 0 && areFiltersDefault ? (
        <div className="col-12">
          <div className="d-flex flex-column align-items-center no-appointments-page">
            {isResponsive ? (
              ""
            ) : (
                <div className="col-md-12 mb-4">
                  <div className="justify-content-between d-flex">
                    <div className="col-md-9">
                      <span className="test-kit-title">
                        Start your health journey with a test{" "}
                      </span>
                      <br />
                      <span className="test-kit-description">
                        Explore key areas of your health, gain insights from
                        your results, and track your progress over time. Browse
                        our tests below to get started.
                      </span>
                    </div>
                    <div className="align-items-center d-flex justify-content-end col-md-3">
                      <Button
                        href="https://www.boots.com/my-health-checked"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-green-color mx-3 pt-2"
                        label="View available tests"
                      />
                    </div>
                  </div>
                </div>
            )}
          </div>
          <div className="card test-kit-carousel-card">
            {isResponsive ? (
              <>
                <div class="justify-content-between d-flex">
                  <div className="mb-2">
                    <h3 className="test-kit-title mb-1 fs-18">
                      Start your health journey with a test{" "}
                    </h3>
                    <p className="test-kit-description fs-14">
                      Explore key areas of your health, gain insights from your
                      results, and track your progress over time. Browse our
                      tests below to get started.
                    </p>
                  </div>
                </div>
                <TestKitCarousel />
                <div>
                  <Button
                    href="https://www.boots.com/my-health-checked"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-green-color w-100 mt-3 fs-16 pt-2"
                    label="View available tests"
                  />
                </div>
              </>
            ) : (
                <TestKitCarousel />
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="test-list-buttons">
            <button
              type="button"
              className={`btn ${
                isHealthTests
                  ? "btn-green ageo-bold"
                  : "btn-outlined-new ageo-medium"
              } ${isMobile ? "fs-14" : "fs-16"}`}
              onClick={() => handleSection(true)}
            >
              Health tests
            </button>
            <button
              type="button"
              className={`btn ${
                isHealthTests
                  ? "btn-outlined-new ageo-medium"
                  : "btn-green ageo-bold"
              } ${isMobile ? "fs-14" : "fs-16"}`}
              onClick={() => handleResultByBiomarker(false)}
            >
              Result by biomarkers
            </button>
          </div>

          {isHealthTests ? (
            <>
              <div
                className={`sorting-buttons-my-result d-flex justify-content-between ${
                  isCustoMobile && "flex-column gap-2"
                }`}
              >
                <div className="status-date d-flex gap-2 justify-content-start">
                  <Dropdown>
                    <Dropdown.Toggle className="my-result-status-btn">
                      <div
                        className="d-flex align-items-center"
                        style={{ padding: "3px 0 0 0" }}
                        title={status}
                      >
                        Status:{" "}
                        <span className="text-pink input-label ms-2">
                          {status}
                        </span>
                      </div>
                      <ChevronDownIcon />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {[
                        "All",
                        "Registered",
                        "Registration pending",
                        "Processing",
                        "Under doctor review",
                        "Results ready",
                        "Unable to report",
                      ].map((s) => (
                        <Dropdown.Item
                          key={s}
                          onClick={() => changeTestStatus(s)}
                        >
                          {s}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>

                  <Dropdown>
                    <Dropdown.Toggle className="my-result-status-btn">
                      <div
                        className="d-flex align-items-center"
                        style={{ padding: "3px 0 0 0" }}
                        title={date}
                      >
                        Date:{" "}
                        <span className="text-pink input-label ms-2">
                          {date}
                        </span>
                      </div>
                      <ChevronDownIcon />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {["All time", "Last 12 months", "Last 30 days"].map(
                        (d) => (
                          <Dropdown.Item
                            key={d}
                            onClick={() => setTestsByDate(d)}
                          >
                            {d}
                          </Dropdown.Item>
                        ),
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                <div className="status-date d-flex w-sm-full">
                  <Dropdown>
                    <Dropdown.Toggle className="my-result-status-btn">
                      <div
                        className="d-flex align-items-center"
                        style={{ padding: "3px 0 0 0" }}
                        title={sortBy}
                      >
                        Sort by:{" "}
                        <span className="text-pink input-label ms-2">
                          {sortBy}
                        </span>
                      </div>
                      <ChevronDownIcon />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {["Most recent", "Least recent"].map((d) => (
                        <Dropdown.Item key={d} onClick={() => setTestOrder(d)}>
                          {d}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              <div
                className={`card container-fluid my-results-page ${
                  testData.length === 0 && "empty-container"
                }`}
              >
                {testData.length === 0 ? (
                  <div>
                    <div className="d-flex flex-column align-items-center no-appointments-page">
                      <div className="image-wrapper d-flex justify-content-center align-items-center">
                        <img src={Calender} alt="No Tests" />
                      </div>
                      <h1
                        className="text-center mb-3"
                        style={{ color: "#003032" }}
                      >
                        No tests available
                      </h1>
                    </div>
                  </div>
                ) : (
                  <table className="table">
                    <tbody>
                      {testData?.map((item, index) => (
                        <tr key={item?.id} className="test-kit-main">
                          <td
                            className={`row test-list-row 
                        ${index === 0 ? "first-td" : ""} 
                        ${index === testData.length - 1 ? "last-td" : ""} 
                        ${isTablet ? "" : "d-flex flex-column"}`}
                          >
                            <div className="col-md-10 p-0">
                              <div className="test-name d-flex">
                                <span>{item?.type_name}</span>{" "}
                                {item?.is_pdf_report_shared_to_pkb ||
                                item?.is_report_shared_to_pkb ? (
                                  <img
                                    className="mx-2"
                                    src={pkbLogo}
                                    alt="pkg icon"
                                  />
                                ) : (
                                  ""
                                )}
                              </div>

                              <div
                                className={`test-details d-flex ${
                                  isMobile ? "flex-column" : "gap-3"
                                }`}
                              >
                                <span className="reported-on">
                                  <p className="d-flex align-items-center">
                                    <div>
                                      <img
                                        alt="calender-icon"
                                        src={calenderIcon}
                                        className="my-result-calender-icon"
                                      />
                                    </div>
                                    <div className="pt-1">
                                      {item?.status_name === "Results Ready" ? (
                                        <>
                                          Reported on :{" "}
                                          <span className="ageo-medium">
                                            {formatDate(item?.review_ts)}
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          Registered date :{" "}
                                          <span className="ageo-medium">
                                            {formatDate(item?.status_ts)}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </p>
                                </span>

                                <span className="primary-barcode">
                                  <p className="d-flex align-items-center">
                                    <div>
                                      <img
                                        alt="barcode-icon"
                                        src={calenderIcon}
                                        className="my-result-calender-icon"
                                      />
                                    </div>
                                    <div className="pt-1">
                                      Barcode :{" "}
                                      <span className="ageo-medium">
                                        {item?.primary_barcode}
                                      </span>
                                    </div>
                                  </p>
                                </span>

                                {item?.postage_tracking && (
                                  <span className="tracking-number">
                                    <p className="d-flex align-items-center">
                                      <div>
                                        <img
                                          alt="barcode-icon"
                                          src={barcodeIcon}
                                          className="barcode-icon"
                                        />
                                      </div>
                                      <div className="pt-1">
                                        Tracking no :{" "}
                                        <span className="ageo-medium">
                                          {item?.postage_tracking}
                                        </span>
                                      </div>
                                    </p>
                                  </span>
                                )}

                                {(item?.test_kit_status === "activating" ||
                                  item?.test_kit_status === "quarantined") && (
                                  <button
                                    type="button"
                                    className="btn-outlined-new test-list-fun-btn"
                                    onClick={() =>
                                      navigate(
                                        `/${item?.detail_link}/resume/${item?.id}/`,
                                      )
                                    }
                                  >
                                    Complete registration
                                  </button>
                                )}

                                {(item?.type === "blood-menopause" ||
                                  item?.type === "blood-polycystic") &&
                                  item?.assessment_id === null &&
                                  item?.test_kit_status !== "activating" &&
                                  item?.test_kit_status !== "quarantined" &&
                                  item?.test_kit_status !== "published" && (
                                    <button
                                      type="button"
                                      className="btn-outlined-new test-list-fun-btn"
                                      onClick={() =>
                                        navigate(`/assessment/${item?.id}/1`)
                                      }
                                    >
                                      Complete assessment
                                    </button>
                                  )}

                                {item?.is_retest_kit === true && (
                                  <button
                                    type="button"
                                    className="btn-outlined-new test-list-fun-btn"
                                    onClick={() =>
                                      navigate(`/account/?testkitId=${item.id}`)
                                    }
                                  >
                                    Order a retest
                                  </button>
                                )}

                                {item?.test_kit_status !== "activating" &&
                                  item?.test_kit_status !== "quarantined" &&
                                  item?.type.includes("dna") && (
                                    <button
                                      type="button"
                                      className="btn-outlined-new test-list-fun-btn"
                                      onClick={() =>
                                        navigate(
                                          `/wellness-assessment/${item?.id}`,
                                        )
                                      }
                                    >
                                      Assessment
                                    </button>
                                  )}

                                {item?.assessment_id === null &&
                                  (item?.test_kit_status === "activated" ||
                                    item?.test_kit_status ===
                                      "quarantine-cleared") &&
                                  !item?.type.includes("dna") &&
                                  !(
                                    item?.type === "blood-menopause" ||
                                    item?.type === "blood-polycystic"
                                  ) && (
                                    <button
                                      type="button"
                                      className="btn-outlined-new test-list-fun-btn"
                                      onClick={() =>
                                        navigate(`/assessment/${item?.id}/1`)
                                      }
                                    >
                                      Complete assessment
                                    </button>
                                  )}
                              </div>
                            </div>

                            <div
                              className={`col-md-2 p-0 d-flex align-items-center justify-content-${
                                isTablet ? "end" : "start"
                              }`}
                            >
                              <div
                                className="test-status-my-result d-flex align-items-center justify-content-center"
                                style={{
                                  backgroundColor: getStatusDetails(
                                    item?.test_kit_status,
                                  )?.bgColor,
                                }}
                                onClick={() => handleClick(item?.id)}
                              >
                                {
                                  getStatusDetails(
                                    item?.test_kit_status?.toLowerCase(),
                                  )?.statusUIName
                                }
                                {getStatusDetails(
                                  item?.test_kit_status?.toLowerCase(),
                                )?.statusUIName === "Registration pending" && (
                                  <img
                                    alt="warning-icon"
                                    className="ms-2 warning-icon"
                                    src={exclamationIcon}
                                  />
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <ResultByBiomarker data={resultByBioMarker} isTablet={isTablet} />
          )}
        </>
      )
  );
};

TestList.propTypes = {
  isAppointment: PropTypes.bool,
};

export default TestList;
